// ===================================================================
// KAIXO - MyBillingModal
// ===================================================================
// "Mis pagos / Facturas" — pantalla que cumple con la guideline de
// Apple App Store 3.1.2 y Google Play que exigen que el usuario pueda
// ver sus pagos pasados y gestionar su suscripción a 1-2 taps desde
// dentro de la app.
//
// Muestra:
//   · Plan actual + fecha de renovación + estado auto-renovación
//   · Lista de facturas (número, fecha, importe, estado)
//   · Lista de pagos recientes (incluso si la factura aún no se emitió)
//   · Botón para abrir PlansModal (cancelar / cambiar plan)
// ===================================================================

import React, { useEffect, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import api from '../../services/api';
import { Icons } from '../Icons/Icons';
import useModalClose from '../../hooks/useModalClose';
import friendlyError from '../../utils/friendlyError';
import PlansModal from '../Plans/PlansModal';
import './MyBillingModal.css';

const I18N = {
    español: {
        title: 'Mis pagos',
        loading: 'Cargando…',
        loadFail: 'No se pudo cargar el historial',
        currentPlan: 'Plan actual',
        memberSince: 'Miembro desde',
        renewsOn: 'Próxima renovación',
        autoRenewOn: 'Auto-renovación activada',
        autoRenewOff: 'Auto-renovación cancelada',
        managePlan: 'Gestionar suscripción',
        cancelRenewal: 'Cancelar auto-renovación',
        cancelRenewalConfirm: '¿Cancelar la auto-renovación? Tu plan seguirá activo hasta el {date}, y después pasarás a FREE. Puedes reactivar la renovación cuando quieras.',
        cancelRenewalSuccess: 'Auto-renovación cancelada. Tu plan sigue activo hasta {date}.',
        cancelRenewalFailed: 'No pudimos cancelar la auto-renovación. Reintenta o contacta soporte@kaixo.ai',
        invoicesH: 'Facturas',
        noInvoices: 'Aún no tienes facturas. Cuando se emita la primera, aparecerá aquí.',
        paymentsH: 'Pagos recientes',
        noPayments: 'No hay pagos registrados.',
        paid: 'Pagada',
        issued: 'Emitida',
        voided: 'Anulada',
        downloadPdf: 'PDF',
        close: 'Cerrar'
    },
    inglés: {
        title: 'My payments',
        loading: 'Loading…',
        loadFail: 'Could not load history',
        currentPlan: 'Current plan',
        memberSince: 'Member since',
        renewsOn: 'Next renewal',
        autoRenewOn: 'Auto-renewal on',
        autoRenewOff: 'Auto-renewal cancelled',
        managePlan: 'Manage subscription',
        cancelRenewal: 'Cancel auto-renewal',
        cancelRenewalConfirm: 'Cancel auto-renewal? Your plan will stay active until {date}, then drop to FREE. You can re-enable it anytime.',
        cancelRenewalSuccess: 'Auto-renewal cancelled. Your plan stays active until {date}.',
        cancelRenewalFailed: 'Could not cancel auto-renewal. Try again or contact soporte@kaixo.ai',
        invoicesH: 'Invoices',
        noInvoices: 'No invoices yet. Your first will appear here once issued.',
        paymentsH: 'Recent payments',
        noPayments: 'No payments yet.',
        paid: 'Paid',
        issued: 'Issued',
        voided: 'Voided',
        downloadPdf: 'PDF',
        close: 'Close'
    }
};

function pickL() {
    try {
        const u = JSON.parse(localStorage.getItem('kaixo_user') || '{}');
        return I18N[u?.language] || I18N.inglés;
    } catch { return I18N.inglés; }
}

function fmtMoney(cents, currency = 'EUR') {
    if (cents == null) return '—';
    const n = (cents / 100).toFixed(2);
    const sym = currency === 'USD' ? '$' : currency === 'EUR' ? '€' : '';
    return sym ? `${sym}${n}` : `${n} ${currency}`;
}

function fmtDate(d) {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString(); } catch { return '—'; }
}

export default function MyBillingModal({ onClose }) {
    const L = pickL();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showPlans, setShowPlans] = useState(false);

    useModalClose(onClose, true);

    // Función re-utilizable para refrescar billing data. Se llama al montar
    // y también cuando el usuario cierra PlansModal (donde puede haber
    // cambiado/cancelado plan), para que MyBillingModal refleje el nuevo
    // estado al instante sin necesidad de cerrarlo y volver a abrirlo.
    const fetchBilling = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get('/plans/me/billing');
            setData(r.data);
            setError(null);
        } catch (e) {
            setError(friendlyError(e, L.loadFail));
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchBilling();
    }, [fetchBilling]);

    // Listener al evento global `kaixo-plan-changed` (lo dispara PlansModal
    // al activar/cambiar/cancelar plan). Refresca al instante.
    useEffect(() => {
        const onPlanChanged = () => fetchBilling();
        window.addEventListener('kaixo-plan-changed', onPlanChanged);
        return () => window.removeEventListener('kaixo-plan-changed', onPlanChanged);
    }, [fetchBilling]);

    const statusLabel = (s) => {
        if (s === 'paid') return L.paid;
        if (s === 'voided') return L.voided;
        return L.issued;
    };

    return ReactDOM.createPortal(
        <div className="mbm-overlay" onClick={onClose}>
            <div className="mbm-modal" onClick={e => e.stopPropagation()}>
                <div className="mbm-header">
                    <h2>{L.title}</h2>
                    <button className="mbm-close" onClick={onClose} aria-label={L.close}>
                        <Icons.Close size={20} />
                    </button>
                </div>

                <div className="mbm-body">
                    {loading && <div className="mbm-center">{L.loading}</div>}
                    {error && <div className="mbm-error">{error}</div>}

                    {!loading && !error && data && (
                        <>
                            {/* Plan actual */}
                            <section className="mbm-section">
                                <h3>{L.currentPlan}</h3>
                                <div className="mbm-plan-card">
                                    <div className="mbm-plan-tier">{data.currentPlan?.tier || 'FREE'}</div>
                                    <div className="mbm-plan-meta">
                                        <div><span>{L.memberSince}:</span> {fmtDate(data.currentPlan?.memberSince)}</div>
                                        {data.currentPlan?.expiresAt && (
                                            <div><span>{L.renewsOn}:</span> {fmtDate(data.currentPlan.expiresAt)}</div>
                                        )}
                                        <div className={`mbm-renew-flag ${data.currentPlan?.autoRenew ? 'on' : 'off'}`}>
                                            {data.currentPlan?.autoRenew ? `✓ ${L.autoRenewOn}` : `✕ ${L.autoRenewOff}`}
                                        </div>
                                    </div>
                                    <div className="mbm-plan-actions">
                                        <button className="mbm-manage-btn" onClick={() => setShowPlans(true)}>
                                            {L.managePlan}
                                        </button>
                                        {/* Botón directo para cancelar la auto-renovación SIN tener
                                            que entrar al PlansModal. Solo aparece si hay plan pagado
                                            con autoRenew activa. Cumple buenas prácticas / DPSD2:
                                            cancelar debe ser tan visible como contratar. */}
                                        {data.currentPlan?.autoRenew && data.currentPlan?.tier && data.currentPlan.tier !== 'FREE' && (
                                            <button
                                                className="mbm-cancel-renewal-btn"
                                                onClick={async () => {
                                                    const dateStr = fmtDate(data.currentPlan?.expiresAt);
                                                    const msg = (L.cancelRenewalConfirm || '').replace('{date}', dateStr);
                                                    if (!window.confirm(msg)) return;
                                                    try {
                                                        await api.post('/plans/cancel');
                                                        const successMsg = (L.cancelRenewalSuccess || '').replace('{date}', dateStr);
                                                        alert(successMsg);
                                                        fetchBilling();
                                                        try {
                                                            window.dispatchEvent(new CustomEvent('kaixo-plan-changed'));
                                                        } catch {}
                                                    } catch (e) {
                                                        alert(L.cancelRenewalFailed);
                                                    }
                                                }}
                                            >
                                                {L.cancelRenewal}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </section>

                            {/* Facturas */}
                            <section className="mbm-section">
                                <h3>{L.invoicesH}</h3>
                                {(!data.invoices || data.invoices.length === 0) ? (
                                    <p className="mbm-empty">{L.noInvoices}</p>
                                ) : (
                                    <ul className="mbm-list">
                                        {data.invoices.map(inv => (
                                            <li key={inv.id} className={`mbm-row mbm-status-${inv.status}`}>
                                                <div className="mbm-row-main">
                                                    <strong>#{inv.number}</strong>
                                                    {inv.description && <div className="mbm-row-desc">{inv.description}</div>}
                                                    <div className="mbm-row-date">{fmtDate(inv.issueDate)}</div>
                                                </div>
                                                <div className="mbm-row-amt">
                                                    <div className="mbm-amt">{fmtMoney(inv.totalCents, inv.currency)}</div>
                                                    <span className={`mbm-tag mbm-tag-${inv.status}`}>{statusLabel(inv.status)}</span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </section>

                            {/* Pagos recientes (webhook procesados) */}
                            {data.payments && data.payments.length > 0 && (
                                <section className="mbm-section">
                                    <h3>{L.paymentsH}</h3>
                                    <ul className="mbm-list">
                                        {data.payments.map((p, i) => (
                                            <li key={i} className="mbm-row">
                                                <div className="mbm-row-main">
                                                    <strong>{p.packageId || p.type}</strong>
                                                    <div className="mbm-row-date">{fmtDate(p.date)}</div>
                                                </div>
                                                <div className="mbm-row-amt">
                                                    <span className="mbm-tag mbm-tag-paid">{L.paid}</span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            )}
                        </>
                    )}
                </div>
            </div>

            {showPlans && <PlansModal onClose={() => setShowPlans(false)} />}
        </div>,
        document.body
    );
}
